import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useEventHandler } from 'hook/event.hook';
import { useLogger } from 'hook/error.hook';
import { useUserInfo } from 'hook/data/userInfo.hook';
import { useCreditInfo } from 'hook/data/creditInfo.hook';
import { fetchAutoLoanOffers } from 'externals/_services/product.service';
import { getPurchaseAutoLoanConfig } from 'helpers/autoOffer.helpers';
import { debounce } from 'helpers/common';
import { hasAutoLoanAccounts } from 'helpers/credit_info';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import { wrapService } from 'actions/service_wrapper.actions';
import { openModalWithUrlReplace } from 'helpers/modal.helper';
import { formatCurrency, unFormatCurrency } from 'helpers/money.helper';

const a_fetchAutoLoanOffers = wrapService(fetchAutoLoanOffers, {
  name: 'fetchAutoLoanOffers',
});

export const useAutoLoanModuleOffers = _ => {
  const MIN_ALLOWED_AMOUNT = 2500;
  const MAX_ALLOWED_AMOUNT = 1000000;
  const sortType = 'lowestMonthlyPayment';
  const [loadingAutoOffers, setLoadingAutoOffers] = useState(true);
  const [autoLoanOffer, setAutoLoanOffer] = useState(null);
  const [autoLoanAmount, setAutoLoanAmount] = useState('$20,000');
  const [autoLoanFormError, setAutoLoanFormError] = useState(null);

  const dispatch = useDispatch();
  const eventHandler = useEventHandler();
  const history = useHistory();
  const { userInfo } = useUserInfo();
  const { creditInfo } = useCreditInfo();
  const { reportAPIError } = useLogger({ name: 'autoLoanPurchaseModule.hook' });

  useEffect(() => {
    const loanTypePurchase = !hasAutoLoanAccounts(creditInfo);
    const validCreditRange =
      creditInfo && creditInfo.creditScore >= 600 ? true : false;

    const unformattedAmount = unFormatCurrency(autoLoanAmount);

    const isValidAmount =
      unformattedAmount &&
      unformattedAmount >= MIN_ALLOWED_AMOUNT &&
      unformattedAmount < MAX_ALLOWED_AMOUNT;

    if (loanTypePurchase && validCreditRange && isValidAmount) {
      setLoadingAutoOffers(true);
      let loanConfig = getPurchaseAutoLoanConfig(
        unformattedAmount,
        creditInfo,
        userInfo,
        dispatch,
        sortType,
      );

      debouncedFetchOffer(
        loanConfig,
        setAutoLoanOffer,
        setLoadingAutoOffers,
        reportAPIError,
        dispatch,
      );
    } else {
      setLoadingAutoOffers(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditInfo, userInfo, autoLoanAmount, dispatch]);

  function onChangeAutoLoanHandle(e) {
    e.preventDefault();
    const { value } = e.target;
    const unformatted = unFormatCurrency(value);

    if (!unformatted) {
      setAutoLoanFormError('Enter valid amount');
    } else if (parseInt(unformatted) < MIN_ALLOWED_AMOUNT) {
      setAutoLoanFormError('Min. $2,500');
    } else if (autoLoanFormError) {
      setAutoLoanFormError(null);
    }

    const dollarAmount = formatCurrency(unformatted);
    if (dollarAmount !== '$NaN' && unformatted < MAX_ALLOWED_AMOUNT) {
      setAutoLoanAmount(dollarAmount);
    }
  }

  const onViewOfferDetailsClick = (key, extraProps) => {
    eventHandler(CLICK, {
      'Click Type': 'Show details',
      'Total Car Cost': autoLoanAmount,
      'Module Name': extraProps.pagePosition,
      'Page Position': extraProps.pagePosition,
      'Module Position': extraProps.modulePosition,
      Vertical: extraProps.vertical,
    });

    openModalWithUrlReplace(history, `/offerDetails/autoLoan/${key}`);
  };

  const onSeeAllOptions = extraProps => {
    eventHandler(CLICK, {
      'Click Type': 'See All Options',
      'Module Name': extraProps.pagePosition,
      'Page Position': extraProps.pagePosition,
      'Module Position': extraProps.modulePosition,
      Vertical: extraProps.vertical,
    });
    history.push('/offers?vertical=AUTO_LOAN');
  };

  return {
    loadingAutoOffers,
    autoLoanOffer,
    autoLoanAmount,
    autoLoanFormError,
    onChangeAutoLoanHandle,
    onViewOfferDetailsClick,
    onSeeAllOptions,
  };
};

const debouncedFetchOffer = debounce(
  (
    loanConfig,
    setAutoLoanOffer,
    setLoadingAutoOffers,
    reportAPIError,
    dispatch,
  ) => {
    dispatch(a_fetchAutoLoanOffers(loanConfig))
      .then(response => {
        let autoLoanOffer = null;
        if (response && response.items.length > 0) {
          autoLoanOffer = response.items
            .filter(item => item.apr && item.monthlyPayment && item.term === 60)
            .sort((a, b) => {
              return a.monthlyPayment - b.monthlyPayment;
            })[0];
        }
        setAutoLoanOffer(autoLoanOffer);
        setLoadingAutoOffers(false);
      })
      .catch(err => {
        setAutoLoanOffer(null);
        reportAPIError(err);
      });
  },
  100,
);
